import React, { Component } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import cx from "classnames";
import ChangeLab from "../../components/DiagnosticsCart/ChangeLab";
import Instructions from "../../components/DiagnosticsCart/Instructions";
import PkgCard from "../../components/DiagnosticsCart/PkgCard";
import TestCard from "../../components/DiagnosticsCart/TestCard";
import BillDetails from "../../components/DiagnosticsCart/BillDetails";
import CartFooter from "../../components/DiagnosticsCart/CartFooter";
import * as actions from "../../actions";
import * as Constants from "../../constants";
import DiagCartLoyaltyItem from "../../components/DiagnosticsCart/DiagCartLoyaltyItem";
import { sendToGTM } from "../../utils/gtmHelper";
import * as AnalyticsHelper from "../../utils/analyticsHelper";
import "./DiagnosticsCart.scss";

class DiagnosticsCart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      reportCharges: 0,
      isCartDataLoaded: false,
      selectedLPVariant: {},
    };
  }

  componentDidMount() {
    this.props.actions.getDiagnosticsCart().then((data) => {
      this.setState({
        isCartDataLoaded: true,
      });
      if (data.data) {
        const { loyalty_details: loyaltyDetails } = data.data;
        if (loyaltyDetails) {
          this.setState({
            selectedLPVariant: get(
              loyaltyDetails,
              "loyalty_pitch_details.plan_selection.variants[0]",
              {}
            ),
          });
        }
      }
    });
  }

  componentWillReceiveProps(nextProps) {
    const {
      cart: { action },
    } = nextProps;
    const oldType = this.props.cart.action
      ? this.props.cart.action.type
      : undefined;
    if (!action || action.type === oldType) {
      return;
    }

    switch (action.type) {
      case Constants.RESET_DIAGNOSTICS_PROMOCODE:
        this.props.actions.getDiagnosticsCart();
        break;
      default:
      // no-op
    }
  }

  setSelectedLab = (labDetails) => {
    this.props.actions.setSelectedLab(labDetails);
  };

  getDiagnosticsCart = () => {
    this.props.actions.getDiagnosticsCart();
  };

  removeItem = (id, type) => {
    if (this.props.testDetails.length === 1) {
      this.props.actions.removeItem(id, type).then(() => {
        const query = this.props.queryParams;
        this.props.actions.getDiagnosticsCart().then((res) => {
          if (res.status === 0 && res.data === null) {
            this.props.actions.clearDiagSearch();
            this.props.actions.searchDiagnosticsProducts(query);
          }
        });
      });
    } else {
      this.props.actions.removeItem(id, type).then(() => {
        this.props.actions.getDiagnosticsCart();
      });
    }
  };

  shouldShowBillDetails = (path) => {
    switch (path) {
      case Constants.DiagnosticsPathMapping.step2_Path:
        return true;
      case Constants.DiagnosticsPathMapping.step3_Path:
        return true;
      case Constants.DiagnosticsPathMapping.step4_Path:
        return false;
      default:
        return false;
    }
  };

  /**
   * The `removeLPItem` function is to handle the removal of a loyalty program item from the diagnostics cart.
   * It triggers the `getDiagnosticsCart` action to refresh the cart data after the removal process.
   * This ensures that the cart reflects the current state without the removed loyalty program item.
   */
  removeLPItem = () => {
    this.props.actions.removeDiagnosticsLoyaltyData();
    this.props.actions.getDiagnosticsCart();
  };
  /**
   * The `addToCartDiagLP` function is responsible for adding a loyalty program (LP) variant to the diagnostics cart.
   * It performs the following steps:
   * 1. Updates the component's state with the selected loyalty program variant.
   * 2. Calls the `setLoyaltyData` action to update the global state with the selected LP variant details and its program ID.
   * 3. If both the program ID and variant ID are provided, it triggers the `getDiagnosticsCart` action with the program and variant IDs.
   *    This action fetches and updates the diagnostics cart to reflect the addition of the selected LP variant.
   *
   * @param {string} programId - The ID of the loyalty program being added to the cart.
   * @param {Object} variant - The selected variant object of the loyalty program, containing details like variant ID.
   */
  addToCartDiagLP = (programId, variant) => {
    this.props.actions.setDiagnosticsLoyaltyData({
      ...variant,
      programId: programId,
    });
    if (programId && variant.id) {
      this.props.actions.getDiagnosticsCart();
      sendToGTM({
        eventLabel: Constants.ADD_TO_CART_EVENT,
        eventDetails: {
          order_type: "loyalty",
          source: "p_ooc_cart",
          item_name: variant.duration,
          item_id: programId,
          item_price: variant.discounted_amount,
          action_type: "add_to_cart",
          ...AnalyticsHelper.getCommonPayloadForAnalytics(
            // eslint-disable-next-line react/prop-types
            this.props.customer,
            this.props.cart,
            this.props.user
          ),
        },
      });
    }
  };
  /**
   * The `onLPVariantUpdate` function is designed to update the selected loyalty program (LP) variant in the diagnostics cart.
   * It performs the following actions:
   * 1. Updates the component's state with the newly selected LP variant.
   * 2. Calls the `getDiagnosticsCart` action with the program ID and the variant ID of the newly selected LP variant.
   *    This action fetches and updates the diagnostics cart to reflect the change in the selected LP variant.
   *
   * @param {string} programId - The ID of the loyalty program whose variant is being updated.
   * @param {Object} variant - The newly selected variant object of the loyalty program, containing details like variant ID.
   */
  onLPVariantUpdate = (programId, variant) => {
    this.props.actions.setDiagnosticsLoyaltyData({
      ...variant,
      programId: programId,
    });
    this.props.actions.getDiagnosticsCart();
    sendToGTM({
      eventLabel: Constants.ADD_TO_CART_EVENT,
      eventDetails: {
        order_type: "loyalty",
        source: "p_ooc_cart",
        item_name: variant.duration,
        item_id: programId,
        item_price: variant.discounted_amount,
        action_type: "add_to_cart",
        ...AnalyticsHelper.getCommonPayloadForAnalytics(
          // eslint-disable-next-line react/prop-types
          this.props.customer,
          this.props.cart,
          this.props.user
        ),
      },
    });
  };

  render() {
    const { cartData, testDetails, error, cartFareData, urlPathName } =
      this.props;
    const { isCartDataLoaded } = this.state;

    const commonLPBenefits = get(cartData, "loyalty_details.benefits", []);

    const lpUserStatus = get(cartData, "loyalty_details.enrolled_status", null);

    const loyaltyPitchDetails = get(
      cartData,
      "loyalty_details.loyalty_pitch_details",
      {}
    );

    const loyaltyItem = get(cartData, "loyalty_details.loyalty_item", {});

    const diagOrderBenefits = get(
      loyaltyPitchDetails,
      "benefits.savings_texts",
      []
    );

    const programId = get(cartData, "loyalty_details.program_id", null);

    const cartPlusSavings = get(
      cartData,
      "loyalty_details.cart_plus_saving",
      0
    );

    const loyaltyVariants = get(
      loyaltyPitchDetails,
      "plan_selection.variants",
      []
    );

    return (
      <div
        className={cx("DiagnosticCartContainer", {
          "pe-loading": this.props.diagnosticsCartLoading,
        })}
      >
        {testDetails && testDetails.length > 0 ? (
          <div className="CartContainer">
            {isCartDataLoaded && (
              <ChangeLab
                selectedLab={cartData.lab_details}
                urlPathName={urlPathName}
              />
            )}
            {!isEmpty(cartData.warnings) && (
              <Instructions instructions={cartData.warnings} />
            )}
            <div className="itemsContainer">
              <div className="testPackageContainer">
                <div className="testPackageHeader">
                  <div className="testPackageHeaderTxt">Item Name</div>
                  <div className="testPackageHeaderTxt">Price</div>
                </div>
                {testDetails &&
                  testDetails.map((test) => {
                    if (test.item_type === "package") {
                      return (
                        <PkgCard
                          key={test.item_id}
                          pkgDetails={test}
                          removeItem={this.removeItem}
                        />
                      );
                    }
                    return (
                      <TestCard
                        key={test.item_id}
                        testDetails={test}
                        removeItem={this.removeItem}
                      />
                    );
                  })}
                {!isEmpty(loyaltyItem) && (
                  <DiagCartLoyaltyItem
                    removeLPItem={this.removeLPItem}
                    diagLoyaltyItem={loyaltyItem}
                    selectedLPVariant={this.props.selectedVariant}
                    onLPVariantUpdate={this.onLPVariantUpdate}
                    programId={programId}
                  />
                )}
                {this.shouldShowBillDetails(urlPathName) && (
                  <BillDetails
                    cashbackDetails={cartData.cashback_details}
                    cartFareData={cartFareData}
                  />
                )}
              </div>
            </div>
            <CartFooter
              items={cartData.test_details ? cartData.test_details.length : 0}
              savings={cartData.total_savings}
              actions={this.props.actions}
              cartFareData={cartFareData}
              loyaltyDetailsPitch={loyaltyPitchDetails}
              loyalty={loyaltyItem}
              loyaltyVariants={loyaltyVariants}
              diagOrderBenefits={diagOrderBenefits}
              selectedDiagLPVariant={this.state.selectedLPVariant}
              commonLPBenefits={commonLPBenefits}
              programId={programId}
              lpUserStatus={lpUserStatus}
              addToCartDiagLP={this.addToCartDiagLP}
              cartPlusSavings={cartPlusSavings}
              selectedSlot={this.props.selectedSlot}
            />
          </div>
        ) : (
          <div className="CartContainerError">{error.message}</div>
        )}
      </div>
    );
  }
}

export default connect(
  (state) => ({
    cartData: state.diagnosticsCart.data,
    testDetails: state.diagnosticsCart.testDetails,
    selectedLab: state.diagnosticsCart.selectedLab,
    error: state.diagnosticsCart.error,
    queryParams: state.product.searchQuery,
    cart: state.diagnosticsCart,
    action: state.action,
    diagnosticsCartLoading: state.diagnosticsCart.loading,
    urlPathName: location.pathname,
    selectedVariant: state.diagnosticsCart.selectedLPVariant,
    cart: state.cart,
    customer: state.customer,
    selectedSlot: state.diagnosticsSlots.selectedSlot,
    user: state.user,
    cartFareData: get(state, "diagnosticsCart.data.fare_details", []).reduce(
      (fareDetailsArr, item) => {
        if (!isEmpty(item)) {
          return [
            ...fareDetailsArr,
            {
              displayTextKey: item.item_key,
              discountedAmount: item.discounted_amount,
              isNegativeValue: item.is_negative_value,
              subTextInfo:
                item.sub_text && item.sub_text.title
                  ? item.sub_text.title
                  : null,
              amount: item.amount,
            },
          ];
        }
        return fareDetailsArr;
      },
      []
    ),
  }),
  (dispatch) => ({
    actions: bindActionCreators(actions, dispatch),
  })
)(DiagnosticsCart);
